import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Pencil, Plus, Trash2 } from "lucide-react";
import * as z from "zod";
import { createMetadataValue, unwrapMetadataSchema, type MetadataValues } from "../../../lib/schedule-presets/metadata-schema";
import { Input } from "../../ui/input";
import { useMetadataEditorNavigationStore } from "../../../stores/metadata-editor-navigation-store";
import { MetadataField } from "./MetadataField";
import { MetadataFormatName } from "./MetadataFormatName";
import { useMetadataForm } from "./MetadataFormContext";
import { MetadataActionButton, type MetadataAction } from "./MetadataActions";

function unusedName(entries: object, base: string) {
  let number = 1;
  while (Object.hasOwn(entries, `${base}_${number}`)) number += 1;
  return `${base}_${number}`;
}

export function MetadataDictionaryEditor({ schema, value, labels, disabled, onChange }: {
  schema: z.ZodObject;
  value: MetadataValues;
  labels: Record<string, string>;
  disabled?: boolean;
  onChange: (value: MetadataValues) => void;
}) {
  const { t } = useTranslation();
  const targetFormat = useMetadataEditorNavigationStore(state => state.format);
  const { values, references } = useMetadataForm();
  const id = useId();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const shape: Record<string, z.ZodType> = schema.shape;
  const catchall = schema.def.catchall as z.ZodType;
  const keys = [...new Set([...Object.keys(shape), ...Object.keys(value)])];
  const definitionMetadata = schema.meta()?.definition;
  const definition = typeof definitionMetadata === "string" ? definitionMetadata : undefined;
  const update = (key: string, next: unknown) => {
    const entries = { ...value, [key]: next };
    if (next === undefined) delete entries[key];
    onChange(entries);
  };
  const add = () => {
    const key = name.trim();
    const unsafeName = ["__proto__", "constructor", "prototype"].includes(key);
    const invalid = key.length === 0 || unsafeName || Object.hasOwn(value, key) || Object.hasOwn(shape, key);
    setNameError(invalid);
    if (invalid) return;
    update(key, createMetadataValue(catchall, values));
    setName("");
  };

  return (
    <div className="space-y-1.5">
      {definition && <p className="text-xs text-neutral-500">{t("metadata.sharedDefinitions")}</p>}
      {keys.map(key => {
        const predefined = Object.hasOwn(shape, key);
        const child = predefined ? shape[key]! : catchall;
        const present = value[key] !== undefined;
        const required = predefined && !child.isOptional();
        const used = references.some(reference => reference.definition === definition && reference.name === key);
        const fieldSchema = unwrapMetadataSchema(child);
        const actions: MetadataAction[] = [];
        if (definition === "formats" && present) {
          actions.push({ label: t("metadata.rename"), icon: Pencil, onClick: () => setRenaming(key) });
        }
        if (present) {
          actions.push({ label: t("metadata.duplicate"), icon: Copy, onClick: () => {
            const reserved = { ...shape, ...value };
            update(unusedName(reserved, key), structuredClone(value[key]));
          } });
        }
        if (present && !required) {
          actions.push({ label: used ? t("metadata.definitionInUse") : t("metadata.remove"),
            icon: Trash2, disabled: used, destructive: true, onClick: () => update(key, undefined) });
        }
        return (
              <MetadataField
                key={key}
                schema={fieldSchema}
                value={value[key]}
                label={labels[key] ? `${labels[key]} · ${key}` : key}
                contentLabel={definition === "formats" ? t("metadata.format.pattern") : undefined}
                labels={labels}
                reveal={definition === "formats" && key === targetFormat ? "focus" : undefined}
                disabled={disabled}
                actions={actions}
                onChange={next => update(key, next)}
              />
        );
      })}
      {renaming !== null && <MetadataFormatName key={renaming} name={renaming} disabled={disabled} onClose={() => setRenaming(null)} />}
      <div className="space-y-2">
        <label htmlFor={id} className="text-xs font-medium">{t("metadata.definitionName")}</label>
        <div className="flex gap-2">
          <Input id={id} value={name} disabled={disabled} onChange={event => {
            setName(event.target.value);
            setNameError(false);
          }} />
          <MetadataActionButton showLabel disabled={disabled}
            action={{ label: t("metadata.addDefinition"), icon: Plus, onClick: add }} />
        </div>
        {nameError && <p className="text-xs text-red-600">{t("metadata.invalidName")}</p>}
      </div>
    </div>
  );
}
